import { useState } from 'react';
import { nanoid } from 'nanoid';
import { useAnalyzeVideoContentMutation, VideoAnalyzerFrameInput } from '@anytag/graphql';
import { ApolloError } from '@apollo/client';
import { Campaign, Rule, TypedVideoAnalyzerPayload, VideoItem } from '../types';

interface UseAnalysisProps {
  activeCampaign: Campaign;
  videoItems: VideoItem[];
  updateActiveCampaignVideos: (videos: VideoItem[]) => void;
  updateVideoStatus: (id: string, updates: Partial<VideoItem>) => void;
}

export const useAnalysis = ({
  activeCampaign,
  videoItems,
  updateActiveCampaignVideos,
  updateVideoStatus
}: UseAnalysisProps) => {
  const { callAnalyzeVideoContent } = useAnalyzeVideoContentMutation();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [processingIndex, setProcessingIndex] = useState<number>(-1);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [activeRules, setActiveRules] = useState<Rule[]>([]);

  const parseRules = (doT: string, dontT: string): Rule[] => {
    const rules: Rule[] = [];
    doT.split('\n').forEach((line) => {
      const clean = line.replace(/^[-\u2022]\s*/, '').trim();
      if (clean) rules.push({ id: nanoid(9), text: clean, type: 'SHOULD' });
    });
    dontT.split('\n').forEach((line) => {
      const clean = line.replace(/^[-\u2022]\s*/, '').trim();
      if (clean) rules.push({ id: nanoid(9), text: clean, type: 'SHOULD_NOT' });
    });
    return rules;
  };

  const runAnalysis = async () => {
    const itemsToProcess = videoItems.filter(
      (i) => (i.status === 'IDLE' || i.status === 'ERROR') && (i.file || (i.frames && i.frames.length > 0))
    );

    if (itemsToProcess.length === 0) {
      if (!videoItems.some((i) => i.status === 'DONE' && !i.file)) {
        setAnalysisError('Please upload new videos to analyze.');
      }
      return;
    }

    const rules = parseRules(activeCampaign.doText, activeCampaign.dontText);

    if (rules.length === 0) {
      setAnalysisError('Please add at least one rule to check.');
      return;
    }

    setActiveRules(rules);
    setIsAnalyzing(true);
    setAnalysisError(null);

    const currentVideos = videoItems.map((item) =>
      item.status === 'DONE' ? item : { ...item, status: 'IDLE' as const, error: undefined }
    );
    updateActiveCampaignVideos(currentVideos);

    try {
      for (let i = 0; i < currentVideos.length; i++) {
        const item = currentVideos[i];
        if (item.status === 'DONE') continue;

        if (!item.frames || item.frames.length === 0) {
          updateVideoStatus(item.id, { status: 'ERROR', error: 'No frames found. Please re-upload.' });
          continue;
        }

        setProcessingIndex(i);
        updateVideoStatus(item.id, { status: 'PROCESSING', extractionProgress: undefined });

        const framesInput: VideoAnalyzerFrameInput[] = item.frames.map((frameDataUrl, idx) => {
          const matches = frameDataUrl.match(/^data:(.+?);base64,(.+)$/);
          const mimeType = matches ? matches[1] : 'image/jpeg';
          const data = matches ? matches[2] : frameDataUrl;

          const totalFrames = item.frames?.length || 0;
          const duration = item.duration || 10;
          const timestamp = (duration / (totalFrames + 1)) * (idx + 1);

          return {
            data,
            mimeType,
            timestamp
          };
        });

        const { data } = await callAnalyzeVideoContent({
          variables: {
            input: {
              context: activeCampaign.context,
              language: activeCampaign.language,
              frames: framesInput,
              rules
            }
          }
        });

        if (data?.analyzeVideoContent?.result) {
          // TODO: better fix type on the BE
          const result = data.analyzeVideoContent.result as TypedVideoAnalyzerPayload;

          updateVideoStatus(item.id, { status: 'DONE', result });
        } else {
          throw new Error('No analysis result returned from backend.');
        }
      }
    } catch (e: unknown) {
      if (e instanceof ApolloError) {
        setAnalysisError(e.message || 'Analysis failed');
        if (processingIndex !== -1 && currentVideos[processingIndex]) {
          updateVideoStatus(currentVideos[processingIndex].id, { status: 'ERROR', error: e.message });
        }
      } else {
        console.error('Analysis failed', e);
        setAnalysisError('Analysis failed');
      }
    } finally {
      setIsAnalyzing(false);
      setProcessingIndex(-1);
    }
  };

  return {
    isAnalyzing,
    processingIndex,
    analysisError,
    activeRules,
    setAnalysisError,
    runAnalysis
  };
};
